import React, {useEffect} from "react";
import { motion } from "framer-motion";
import Breadcrumb from "../Components/Breadcrumb";
import Cta from "../Components/Cta";

const PrivacyPolicy=()=>{

      const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
    filter: "blur(8px)",
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariant = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

useEffect(() => {
    document.title =
      "Privacy Policy | ONBBITS ";

    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Read the ONBBITS Privacy Policy to understand how we collect, use, store and protect your personal information and business data."
      );
    }

     const canonical = document.querySelector("link[rel='canonical']");
  if (canonical) {
    canonical.setAttribute("href", "https://onbbits.io/privacy-policy");
  }

  const ogTitle = document.querySelector("meta[property='og:title']");
  if (ogTitle) {
    ogTitle.setAttribute(
      "content",
      "Privacy Policy | ONBBITS "
    );
  }

    const ogDesc = document.querySelector("meta[property='og:description']");
  if (ogDesc) {
    ogDesc.setAttribute(
      "content",
      "Read the ONBBITS Privacy Policy to understand how we collect, use, store and protect your personal information and business data."
    );
  }

  const ogUrl = document.querySelector("meta[property='og:url']");
  if (ogUrl) {
    ogUrl.setAttribute("content", "https://onbbits.io/privacy-policy");
  }

    const ogImage = document.querySelector("meta[property='og:image']");
  if (ogImage) {
    ogImage.setAttribute(
      "content",
      "https://onbbits.io/Images/hero-img.webp"
    );
  }

    const twitterTitle = document.querySelector("meta[name='twitter:title']");
  if (twitterTitle) {
    twitterTitle.setAttribute(
      "content",
      "Privacy Policy | ONBBITS "
    );
  }

    const twitterDesc = document.querySelector(
    "meta[name='twitter:description']"
  );
  if (twitterDesc) {
    twitterDesc.setAttribute(
      "content",
      "Read the ONBBITS Privacy Policy to understand how we collect, use, store and protect your personal information and business data."
    );
  }

    const twitterImage = document.querySelector("meta[name='twitter:image']");
  if (twitterImage) {
    twitterImage.setAttribute(
      "content",
      "https://onbbits.io/Images/hero-img.webp"
    );
  }
  }, [])

    return(
        <>
        {/**BREADCRUMB */}
        <Breadcrumb title="Privacy Policy" />

        <motion.section
            className="policy-section container"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}>

         <motion.h2 className="section-heading" variants={fadeUp}>Summary of Key Points</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
        This summary provides the key points of our Privacy Policy. Please read the full sections below for complete details. By using ONBBITS, you agree to the practices described in this policy.
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            We collect information you provide (name, email, phone, business details) and information generated automatically when you use our platform (usage data, logs, device info).
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            We use your information to operate and improve the ONBBITS platform, process payments, send service communications, and fulfill legal obligations.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            We do not sell your personal information to third parties.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            We share information only with trusted service providers (payment, infrastructure, WhatsApp BSP) under strict data processing agreements.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            Your end-customer data (contacts, conversation history) is processed on your behalf—you remain the data controller for that data.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            You have the right to access, correct, or delete your personal data at any time by contacting <a href="mailto:privacy@onbbits.io">privacy@onbbits.io</a>.
        </motion.li>
    </motion.ul>

    <motion.h2 className="section-heading" variants={fadeUp}>1. Who We Are</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
        ONBBITS is a WhatsApp-first automation and communication platform operated by ONBBITS Innovation Pvt. Ltd. (we, us, or our). We are incorporated in India and provide cloud-based SaaS services to businesses across India and internationally.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        This Privacy Policy applies to all users of the ONBBITS platform, including administrators, agents, and any individual who accesses or uses our website at <a href="https://onbbits.io">onbbits.io</a> or any of our sub-domains.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        If you do not agree with the terms of this Privacy Policy, please discontinue use of the Service immediately.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>2. What Information Do We Collect?</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
      We collect information you give us directly, information generated automatically as you use the platform, and limited information from third-party sources.
    </motion.p>

    <motion.h3 className="card-heading" variants={fadeUp}>2.1 Information You Provide to Us</motion.h3>
    <motion.p className="section-text" variants={itemVariant}>
        We collect personal information that you voluntarily provide when you register an account, set up an App, purchase a subscription, or contact us for support.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">Account & Identity Information</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Full name and display name</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Email address (used as primary account identifier)</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Mobile phone number</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Business name and type</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Country of operation</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Username and encrypted password</motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">Business & WhatsApp Onboarding Information</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>WhatsApp Business Account (WABA) ID, Phone Number ID, and Business ID</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Business display name submitted during WABA registration</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Facebook Business Manager ID (used for WABA verification through our BSP, AdKrity)</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Business profile details: description, website URL, business category, and profile photo</motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">Billing & Payment Information</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Company legal name and billing address (including city, state, postal code, country)</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>GST/Tax Identification Number (for Indian businesses)</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Payment instrument details—entered directly into Razorpay, NOT stored on ONBBITS servers</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Invoice history and subscription status</motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">End-Customer Data (Processed on Your Behalf)</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Contact names and WhatsApp phone numbers</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Custom attributes, tags, and notes assigned to contacts</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Opt-in and opt-out status for messaging</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Conversation history visible within the ONBBITS Inbox</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Campaign send lists and segmentation data</motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">Support & Communications</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Support ticket content, including title, description, and attachments</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Emails, chat messages, or other communications you send to our team</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Feedback and survey responses</motion.li>
    </motion.ul>

    <motion.h3 className="card-heading" variants={fadeUp}>2.2 Information Collected Automatically</motion.h3>
    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">Log and Usage Data</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>IP address and approximate geographic location (country/city level)</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Browser type, version, and operating system</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Pages viewed, features accessed, and navigation paths within the platform</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Date and time of access, session duration, and frequency of use</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Error reports and crash logs</motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">Platform Event Data (PostHog Analytics)</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Dashboard and page views</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Feature interactions (e.g., campaign created, template submitted, contact imported)</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Button clicks and form submissions for platform improvement</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Activated only after you provide explicit consent through the in-app analytics consent prompt</motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">WhatsApp Message Delivery Metadata</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Message status events received from Meta's Cloud API: sent, delivered, read, failed</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Timestamps associated with each delivery event</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Failure reason codes for undelivered messages</motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">Cookies and Tracking Technologies</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Session cookies: required to maintain your logged-in state</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Preference cookies: to remember your language and UI preferences</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Analytics cookies: set by PostHog, only after consent</motion.li>
    </motion.ul>

    <motion.h3 className="card-heading" variants={fadeUp}>2.3 Information Received from Third Parties</motion.h3>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Meta/WhatsApp:</strong> WABA ID, business verification status, phone number registration status, and template approval status.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Razorpay:</strong> Payment confirmation, transaction ID, and masked payment details following a successful transaction.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>AdKrity (BSP):</strong> WABA connection status and API credentials following onboarding.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Google Sign-In:</strong> Your name, email address, and profile picture as shared by your Google account.
        </motion.li>
    </motion.ul>

    <motion.h2 className="section-heading" variants={fadeUp}>3. How Do We Use Your Information?</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
     We use your information to deliver and improve the ONBBITS Service, process payments, communicate with you, and comply with legal obligations. We do not use your data for purposes beyond what is described here without your consent.
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Service Delivery:</strong> Create and manage your account, enable messaging features, display conversation history, and manage wallet balances in real time.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Billing & Payments:</strong> Process subscription payments via Razorpay, calculate GST, generate invoices, and monitor wallet balances.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Communications:</strong> Send transactional emails, respond to support tickets, and notify you of service updates and policy changes.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Platform Improvement:</strong> Analyse anonymised usage data, monitor performance, conduct A/B testing, and use PostHog analytics with your consent.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Security & Fraud Prevention:</strong> Detect unauthorised access, enforce role-based access controls, and investigate security incidents.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Legal & Compliance:</strong> Comply with applicable laws, respond to lawful requests, and maintain records required under Indian statutes.
        </motion.li>
    </motion.ul>

    <motion.p className="section-text" variants={itemVariant}>
        <strong className="special-strong">What We Do NOT Do With Your Data</strong>
    </motion.p>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>We do not sell your personal information to any third party.</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>We do not use your end-customer data for any purpose other than delivering the Service to you.</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>We do not use your data for targeted advertising on behalf of third-party advertisers.</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>We do not share your data with competitors or unrelated businesses.</motion.li>
    </motion.ul>

    <motion.h2 className="section-heading" variants={fadeUp}>4. Legal Basis for Processing</motion.h2>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Contractual Necessity:</strong> Processing required to deliver the Service you have subscribed to (e.g., account creation, messaging, billing).
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Legitimate Interests:</strong> Analytics and platform improvement, security monitoring, and fraud prevention—where our interests do not override your rights.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Legal Obligation:</strong> Retention of financial records, response to regulatory inquiries, and compliance with Indian tax and corporate law.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Consent:</strong> Analytics tracking via PostHog, and any marketing communications. You may withdraw consent at any time without affecting prior processing.
        </motion.li>
    </motion.ul>

    <motion.h2 className="section-heading" variants={fadeUp}>5. When and With Whom Do We Share Your Information?</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
     We do not sell your data. We share information only with trusted service providers who help us operate the platform, and only as necessary to deliver the Service.
    </motion.p>

    <motion.h3 className="card-heading" variants={fadeUp}>5.1 Service Providers</motion.h3>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Razorpay:</strong> Payment processing for subscription fees and wallet recharges. Razorpay stores payment instrument details; ONBBITS only receives transaction confirmation.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>AdKrity (BSP):</strong> Our WhatsApp Business Solution Provider. WABA connection details and message delivery requests are routed through AdKrity's infrastructure.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Meta (WhatsApp Cloud API):</strong> Message delivery, template approval, and WABA management. Data exchanged is governed by Meta's Business Terms.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>PostHog:</strong> Product analytics. Event data is transmitted to PostHog only after user consent. Data is anonymised before analysis.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Cloud Infrastructure:</strong> Hosting, storage, and database services. Servers are located in India or within AWS/GCP regions with appropriate data residency controls.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>ERPNext (Internal ERP):</strong> Used internally for subscription management, invoicing, support ticket routing, and financial reconciliation.
        </motion.li>
    </motion.ul>

    <motion.h3 className="card-heading" variants={fadeUp}>5.2 Business Transfers</motion.h3>
    <motion.p className="section-text" variants={itemVariant}>
        In the event of a merger, acquisition, restructuring, or sale of all or part of our business, your information may be transferred to the acquiring entity. We will notify you via email or a prominent in-platform notice if such a transfer materially affects how your data is used.
    </motion.p>

    <motion.h3 className="card-heading" variants={fadeUp}>5.3 Legal Disclosure</motion.h3>
    <motion.p className="section-text" variants={itemVariant}>
        We may disclose your information to government authorities, regulators, or courts when required by law, a valid court order, or regulatory directive. We will notify you of such disclosure where legally permitted to do so.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>6. How Long Do We Keep Your Information?</motion.h2>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Account & profile data:</strong> Active subscription + 30 days after termination, then deleted or anonymised.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Financial records (invoices, payments):</strong> 7 years as required under Indian Income Tax Act and GST regulations.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Support ticket data:</strong> 3 years from resolution to support any follow-up disputes or audits.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Analytics event data (PostHog):</strong> 36 months from the date of collection.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>WhatsApp conversation metadata:</strong> 12 months, after which logs are archived or deleted.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Inactive wallet credits:</strong> 365 days of inactivity wallet balances may expire with prior notification.
        </motion.li>
    </motion.ul>

    <motion.h2 className="section-heading" variants={fadeUp}>7. How Do We Keep Your Information Safe?</motion.h2>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>HTTPS/TLS encryption for all data in transit between your browser and our servers.</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Encrypted storage for sensitive credentials and API keys.</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Audit logs for significant platform actions (ticket updates, agent assignments, billing events).</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Role-based access control (RBAC): Agents access only assigned data; Admins have full app-level control.</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Server-side verification of all payment signatures from Razorpay.</motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>Regular security reviews and access control audits.</motion.li>
    </motion.ul>
    <motion.p className="section-text" variants={itemVariant}>
        Despite these measures, no system is completely immune to breaches. If you suspect unauthorised access, please contact us immediately at <a href="mailto:support@onbbits.io">support@onbbits.io</a>.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>8. Cookies and Tracking Technologies</motion.h2>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Strictly Necessary:</strong> Required to maintain your session and enable core platform functionality. These cannot be disabled.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Preference Cookies:</strong> Store your language setting and UI preferences across sessions.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Analytics Cookies:</strong> Track feature usage via PostHog for product improvement. Activated only with your explicit consent.
        </motion.li>
    </motion.ul>
    <motion.p className="section-text" variants={itemVariant}>
        We do not use advertising cookies or third-party retargeting technologies on the ONBBITS platform.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>9. Social Logins</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
        ONBBITS offers the option to register or log in using your Google account if you choose to do so. Google will share your name, email address, and profile picture with us. We use this information solely to create and manage your ONBBITS account.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        If you attempt to log in using Google with an email address already registered via email/password, the platform will display an appropriate error message to prevent account duplication.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        We are not responsible for the data practices of Google. Please review <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google's Privacy Policy</a> to understand how they handle your data.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>10. Your Privacy Rights</motion.h2>
    <motion.h3 className="card-heading" variants={fadeUp}>10.1 Rights Available to All Users</motion.h3>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Access:</strong> Request a copy of the personal information we hold about you.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Correction:</strong> Request correction of inaccurate or incomplete information.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Deletion:</strong> Request deletion of your personal data subject to our legal retention obligations.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Withdraw Consent:</strong> Withdraw consent to analytics tracking or marketing communications at any time.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Opt-Out of Marketing:</strong> Unsubscribe from promotional emails at any time. You will still receive transactional messages.
        </motion.li>
    </motion.ul>

    <motion.h3 className="card-heading" variants={fadeUp}>10.2 Additional Rights for EEA / UK Users (GDPR)</motion.h3>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Data Portability:</strong> Receive your data in a structured, machine-readable format.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Restriction:</strong> Request that we restrict processing of your data in certain circumstances.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Object:</strong> Object to processing based on legitimate interests.
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>Lodge a Complaint:</strong> File a complaint with your local data protection supervisory authority.
        </motion.li>
    </motion.ul>

    <motion.h3 className="card-heading" variants={fadeUp}>10.3 How to Exercise Your Rights</motion.h3>
    <motion.p className="section-text" variants={itemVariant}>
        Email us at <a href="mailto:privacy@onbbits.io">privacy@onbbits.io</a> from your registered email address with the subject line "Data Rights Request". Please include your full name, App ID, and a clear description of your request. We will respond within 30 days.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>11. Do-Not-Track Signals</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
        Some web browsers allow you to activate a Do-Not-Track (DNT) setting to signal that you do not want your online behaviour tracked. At present, no universally accepted standard exists for responding to DNT signals, and ONBBITS does not currently respond to DNT browser signals. We will update this section if a recognised standard is adopted that we are required to follow.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>12. Children's Privacy</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
        The ONBBITS platform is a business tool intended for use by individuals aged 18 and above. We do not knowingly collect personal information from persons under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href="mailto:support@onbbits.io">support@onbbits.io</a> and we will promptly delete such data.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>13. India - Digital Personal Data Protection Act (DPDP Act, 2023)</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
        As an Indian company, ONBBITS Innovation Pvt. Ltd. is committed to compliance with India's Digital Personal Data Protection Act, 2023.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        We process your personal data only for lawful purposes and with appropriate consent or legitimate use cases as defined under the Act.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        You have the right to obtain a summary of your personal data being processed, to correct or erase your data, and to nominate a representative for exercising these rights in the event of your death or incapacity.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        We have designated a Data Protection Point of Contact reachable at <a href="mailto:privacy@onbbits.io">privacy@onbbits.io</a>.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        We will notify you and the relevant Data Protection Board of India of any significant data breach in accordance with the timelines prescribed under the Act.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>14. Updates to This Privacy Policy</motion.h2>
    <motion.p className="section-text" variants={itemVariant}>
        We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or platform features. The updated Effective Date at the top of this document will indicate when the most recent revision was made.
    </motion.p>
    <motion.p className="section-text" variants={itemVariant}>
        For material changes, we will notify you by email or by displaying a prominent notice within the ONBBITS platform at least 14 days before the changes take effect.
    </motion.p>

    <motion.h2 className="section-heading" variants={fadeUp}>15. Contact Us</motion.h2>
    <motion.ul variants={itemVariant}>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>PRIVACY & DATA REQUESTS:</strong> <a href="mailto:privacy@onbbits.io">privacy@onbbits.io</a>
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>GENERAL SUPPORT:</strong> <a href="mailto:support@onbbits.io">support@onbbits.io</a>
        </motion.li>
        <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
            <strong>REGISTERED ADDRESS:</strong> ONBBITS Innovation Pvt. Ltd., 210, Pramuk Square, Airport Ring Road, Bhuj - Kutch Gujarat - India - 370001
        </motion.li>
    </motion.ul>
    <motion.p className="section-text" variants={itemVariant}>
        We aim to respond to all privacy-related enquiries within 7 business days.
    </motion.p>
            </motion.section>

        {/**CTA SECTION */}
        <Cta />
        </>
    )
}
export default PrivacyPolicy;